import joi from 'joi';

import type { Radians } from '../utils/angles';
import { type Vector, isNonZero, vector } from './vector';

export const POINT_SVG_MATCH_STR =
  '(-?\\d+(?:\\.\\d+)?)(?:\\s+|\\s*,\\s*)(-?\\d+(?:\\.\\d+)?)';
export const POINT_SVG_MATCH_RE = new RegExp(`^${POINT_SVG_MATCH_STR}$`);
type PointSvgRegExpMatch = [all: string, x: string, y: string];

export interface Point {
  readonly x: number;
  readonly y: number;
}

export const pointSchema = joi.object().keys({
  x: joi.number().required(),
  y: joi.number().required(),
});

export interface WeightedPoint extends Point {
  readonly weight: number;
}

export function appendToArray(array: number[], { x, y }: Point): number[] {
  return [...array, x, y];
}

export function create(x = 0, y = 0): Point {
  return { x, y };
}

export function equals(a: Point, b: Point): boolean {
  return a.x === b.x && a.y === b.y;
}

export const flipHorizontal = (p: Point): Point => ({ x: -p.x, y: p.y });

export const flipVertical = (p: Point): Point => ({ x: p.x, y: -p.y });

export function fromArray(arr: [number, number]): Point {
  const [x, y] = arr;
  return { x, y };
}

export function fromLine(
  arr: [number, number, ...number[]],
): [Point, ...Point[]];
export function fromLine(arr: number[]): Point[];
export function fromLine(arr: number[]): Point[] {
  const result: Point[] = [];
  if (arr.length % 2 !== 0) {
    throw new Error('Number array representing a line must have even length');
  }
  for (let i = 0; i < arr.length; i += 2) {
    result.push(create(arr[i], arr[i + 1]));
  }
  return result;
}

/**
 * Create an instance from SVG's `d` attribute
 */
export function fromSvgString(d: string): Point {
  const match = d.trim().match(POINT_SVG_MATCH_RE);
  if (!match) {
    throw new Error(`Unexpected SVG path: ${JSON.stringify(d)}`);
  }
  const [, x, y] = match as PointSvgRegExpMatch;
  return create(+x, +y);
}

/**
 * Get centroid - also known as "center of gravity" or "center of mass"
 */
export function getCentroid(points: Point[]): Point | null {
  if (!points.length) {
    return null;
  }
  const sum = points.reduce(
    (s, point) => ({ x: s.x + point.x, y: s.y + point.y }),
    { x: 0, y: 0 },
  );
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/**
 * Get centroid from weighted points
 */
export function getWeightedCentroid(points: WeightedPoint[]): Point | null {
  const sum = points.reduce(
    (s, point) => {
      if (point.weight > 0) {
        return {
          x: s ? s.x + point.weight * point.x : point.weight * point.x,
          y: s ? s.y + point.weight * point.y : point.weight * point.y,
          weight: s ? s.weight + point.weight : point.weight,
        };
      }
      return s;
    },
    undefined as WeightedPoint | undefined,
  );
  return sum ? { x: sum.x / sum.weight, y: sum.y / sum.weight } : null;
}

export function getD({ x, y }: Point, fractionDigits = 0): string {
  return `${x.toFixed(fractionDigits)} ${y.toFixed(fractionDigits)}`;
}

/**
 * Squared distance is computationally cheaper and often enough e.g. when
 * you just need to compare distances to each other.
 */
export function getSquaredDistance(a: Point, b: Point): number {
  const x = a.x - b.x;
  const y = a.y - b.y;
  return x * x + y * y;
}

export function getDistance(a: Point, b: Point): number {
  return Math.sqrt(getSquaredDistance(a, b));
}

/**
 * Create new point in given distance and direction from original point
 *
 * @param start
 * @param direction
 * @param distance if omitted, direction vector size is used
 */
export function createOnVector(
  start: Point,
  direction: Vector,
  distance?: number,
): Point {
  if (distance === 0 || !isNonZero(direction)) {
    return start;
  }
  if (distance !== undefined) {
    return createOnVector(start, vector.setMagnitude(direction, distance));
  }

  return {
    x: start.x + direction.x,
    y: start.y + direction.y,
  };
}

export const rotate = (p: Point, center: Point, angle: Radians): Point => {
  const x = p.x - center.x;
  const y = p.y - center.y;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: center.x + x * cos - y * sin,
    y: center.y + x * sin + y * cos,
  };
};

export const shift = (p: Point, { x = 0, y = 0 }: Partial<Point>): Point => ({
  x: p.x + x,
  y: p.y + y,
});

export function toArray({ x, y }: Point): [number, number] {
  return [x, y];
}

export function toLine(...points: Point[]): number[] {
  return points.reduce(appendToArray, []);
}

export function pointToString({ x, y }: Point, fractionDigits = 2): string {
  return `Point (${x.toFixed(fractionDigits)}, ${y.toFixed(fractionDigits)})`;
}

export const zero = { x: 0, y: 0 };

export const point = {
  appendToArray,
  create,
  equals,
  flipHorizontal,
  flipVertical,
  fromArray,
  fromLine,
  fromSvgString,
  getCentroid,
  getD,
  getDistance,
  getSquaredDistance,
  getWeightedCentroid,
  createOnVector,
  rotate,
  shift,
  toArray,
  toLine,
  toString: pointToString,
  zero,
};
